import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ProjectList from "./ProjectList";
import DependentOnTab from "./DependentOnTab";
import MyConsumerTab from "./MyConsumerTab";

interface RelationshipProps {
  open: boolean;
  onClose: () => void;
  dependencies: any[];
  onUpdateDependencies: (dependencies: any[]) => void;
  projectId: string;
  projectList: any[];
  relatedBugs?: any[];
  isTaskUpdate?: boolean;
  currentTaskId?: string; // Add current task ID
}

const Relationship: React.FC<RelationshipProps> = ({
  open,
  onClose,
  dependencies,
  onUpdateDependencies,
  projectId,
  projectList,
  relatedBugs = [],
  isTaskUpdate = false,
  currentTaskId,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedProject, setSelectedProject] = useState<any>(null);

  // Set current project as default selected when modal opens
  useEffect(() => {
    if (open && projectId && projectList.length > 0) {
      const currentProject = projectList.find((p) => p._id === projectId);
      if (currentProject) {
        setSelectedProject(currentProject);
      }
    }
  }, [open, projectId, projectList]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleProjectSelect = (project: any) => {
    setSelectedProject(project);
  };

  const handleBack = () => {
    setSelectedProject(null);
  };

  // Direct update - no need for local state
  const handleDependenciesUpdate = (newDependencies: any[]) => {
    onUpdateDependencies(newDependencies);
  };

  // Get projects that have dependencies
  const getProjectsWithDependencies = () => {
    const projectIds = Array.from(
      new Set(
        dependencies
          .map((dep) => dep.project?._id || dep.projectId)
          .filter(Boolean)
      )
    );
    return projectIds;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        "& .MuiDialog-paper": {
          width: "80vw",
          height: "80vh",
          maxWidth: "none",
          maxHeight: "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* {selectedProject && (
            <IconButton onClick={handleBack} size="small">
              <ArrowBackIcon />
            </IconButton>
          )} */}
          <Typography
            variant="h6"
            sx={{ color: "text.secondary", fontWeight: "bold" }}
          >
            {t("Relationship for")} {selectedProject?.name}
          </Typography>
          {/* <Typography variant="body2" sx={{ color: "#999" }}>
            {t("Description about this section")}
          </Typography> */}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ p: 0, display: "flex", flexDirection: "column", height: "100%" }}
      >
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                fontSize: "14px",
              },
            }}
          >
            <Tab label={t("Dependent on")} />
            {isTaskUpdate && <Tab label={t("My Consumers")} />}
          </Tabs>
        </Box>

        {/* Content Area */}
        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {activeTab === 1 && isTaskUpdate ? (
            // My Consumers tab - full width, no project list
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <MyConsumerTab
                relatedBugs={relatedBugs}
                currentTaskId={currentTaskId}
              />
            </Box>
          ) : (
            // Dependent On tab - with project list
            <>
              {/* Left Side - Project List (20% width) */}
              <Box
                sx={{
                  width: "20%",
                  borderRight: 1,
                  borderColor: "divider",
                  minWidth: "300px",
                }}
              >
                <ProjectList
                  projects={projectList}
                  selectedProject={selectedProject}
                  onProjectSelect={handleProjectSelect}
                  currentProjectId={projectId}
                  tasksFromProjects={getProjectsWithDependencies()}
                />
              </Box>

              {/* Right Side - Content (80% width) */}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <DependentOnTab
                  selectedProject={selectedProject}
                  dependencies={dependencies}
                  onUpdateDependencies={handleDependenciesUpdate}
                  currentProjectId={projectId}
                />
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default Relationship;
